// Shared helpers for the Scheduling feature (route self-scheduling).
// Datetime rule: the server speaks fleet-local wall time with NO timezone
// suffix ("YYYY-MM-DDTHH:MM:SS"). Never use toISOString() — it shifts to UTC.

export function fmtTime(v) {
  if (!v) return '';
  const d = new Date(v);
  return isNaN(d.getTime())
    ? String(v)
    : d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

// Group-header label for a service date, e.g. "Mon, Jun 9".
export function fmtServiceDate(v) {
  if (!v) return 'Unscheduled';
  // ServiceDate is YYYY-MM-DD — parse as local to avoid a UTC off-by-one.
  const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/);
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(v);
  return isNaN(d.getTime())
    ? String(v)
    : d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
}

/**
 * Wall-clock parts of a server datetime, with NO timezone conversion.
 *
 * Availability/route times are naive wall-clock values (SQL `datetime2`, which
 * carries no offset — see driverScheduling.js). The mssql driver runs with
 * tedious' default `useUTC: true`, so those naive digits come back as a Date
 * whose *UTC* accessors hold them, and JSON serialization then stamps a "Z" on
 * them: `"2026-08-07T09:30:00.000Z"` means 09:30 **local wall-clock**, not
 * 09:30 UTC.
 *
 * Reading that back with `new Date(v).getHours()` takes the false "Z" at face
 * value and re-projects into the browser's zone, shifting every time by the
 * local UTC offset (+5:30 in IST). Writes don't do this — composeDateTime()
 * emits bare digits — so times drifted on every reload. That asymmetry is the
 * bug; this helper is the symmetric read.
 */
export function wallClockParts(v) {
  if (!v) return null;
  if (typeof v === 'string') {
    // Handles "YYYY-MM-DDTHH:MM[:SS][.mmm][Z]" and the space-separated SQL form,
    // taking the digits exactly as written and ignoring any offset marker.
    const m = v.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/);
    if (m) {
      return {
        year: +m[1], month: +m[2], day: +m[3],
        hours: +m[4], minutes: +m[5], seconds: +(m[6] || 0),
      };
    }
  }
  const d = v instanceof Date ? v : new Date(v);
  if (isNaN(d.getTime())) return null;
  // UTC accessors, not local: with useUTC the naive digits live there.
  return {
    year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate(),
    hours: d.getUTCHours(), minutes: d.getUTCMinutes(), seconds: d.getUTCSeconds(),
  };
}

/** Wall-clock "HH:MM" from a server datetime — no timezone shift. */
export function toWallClockHM(v) {
  const p = wallClockParts(v);
  if (!p) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(p.hours)}:${pad(p.minutes)}`;
}

// Convert a server datetime (local, no TZ) to a datetime-local input value
// (YYYY-MM-DDTHH:MM).
export function toInputValue(v) {
  const p = wallClockParts(v);
  if (!p) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hours)}:${pad(p.minutes)}`;
}

// Full server shape from a Date or datetime-local string: "YYYY-MM-DDTHH:MM:SS".
export function toFleetLocalDateTime(v) {
  if (!v) return '';
  if (typeof v === 'string') {
    // Already "YYYY-MM-DDTHH:MM" or "YYYY-MM-DDTHH:MM:SS" — just normalize seconds.
    const m = v.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})(:\d{2})?/);
    if (m) return `${m[1]}${m[2] || ':00'}`;
  }
  const d = new Date(v);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

// Template times are "HH:MM" or "HH:MM:SS" — normalize to "HH:MM:SS" for PUT.
// Returns null for empty/cleared values (null clears the field server-side).
export function toTemplateTime(v) {
  if (v == null || v === '') return null;
  const m = String(v).match(/^(\d{2}:\d{2})(:\d{2})?$/);
  return m ? `${m[1]}${m[2] || ':00'}` : null;
}

// "HH:MM:SS" → "HH:MM" for <input type="time"> values.
export function toTimeInputValue(v) {
  if (!v) return '';
  const m = String(v).match(/^(\d{2}:\d{2})/);
  return m ? m[1] : '';
}

// Compact time for calendar chips, e.g. "7a", "7:30a", "3p".
export function fmtTimeShort(v) {
  // Wall-clock, same as the editor — the calendar chips were shifting by the
  // browser's UTC offset too, so a chip and its editor disagreed.
  const p = wallClockParts(v);
  if (!p) return '';
  const h = p.hours;
  const m = p.minutes;
  const ap = h < 12 ? 'a' : 'p';
  let hh = h % 12;
  if (hh === 0) hh = 12;
  return m ? `${hh}:${String(m).padStart(2, '0')}${ap}` : `${hh}${ap}`;
}

// Compact range for a route chip, e.g. "7a–3p".
export function fmtRangeShort(start, end) {
  const s = fmtTimeShort(start);
  const e = fmtTimeShort(end);
  if (s && e) return `${s}–${e}`;
  return s || e || '';
}

// Route status for the calendar color coding (4 states from the sketch):
// completed | current | claimed | available (unclaimed baseline).
export function routeStatus(route, now = new Date()) {
  if (route.claimed) {
    const start = route.StartDateTime ? new Date(route.StartDateTime) : null;
    const end = route.EndDateTime ? new Date(route.EndDateTime) : null;
    if (end && end < now) return 'completed';
    if (start && end && start <= now && now <= end) return 'current';
    return 'claimed';
  }
  return 'available';
}

// GUID-safe compare — server mixes casings (and occasionally types).
export function sameId(a, b) {
  if (a == null || b == null) return false;
  return String(a).toLowerCase() === String(b).toLowerCase();
}

// Local YYYY-MM-DD for a Date (calendar keys / serviceDate params).
export function toServiceDate(d) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export const WEEKDAY_KEYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

// True when two [start, end] datetime ranges overlap (string compare works for
// same-shape fleet-local datetimes).
export function rangesOverlap(startA, endA, startB, endB) {
  if (!startA || !endA || !startB || !endB) return false;
  return startA < endB && startB < endA;
}

// Status of a calendar day for the week strip / month chips.
// routes: routes on that day (claimed = has DriverId/VehicleId per server shape).
// templateDay: the template's weekday entry ({start,end,...} | undefined).
export function dayStatus(routesForDay, templateDay) {
  if (routesForDay?.some((r) => r.IsInactive === true || r.IsInactive === 1)) {
    if (routesForDay.every((r) => r.IsInactive === true || r.IsInactive === 1)) return 'closed';
  }
  if (routesForDay?.some((r) => r.claimed)) return 'claimed';
  if (routesForDay?.length) return 'planned';
  if (templateDay?.start && templateDay?.end) return 'planned';
  return 'off';
}

// Distinct trips, not steps: one trip = a pickup step + a delivery step, same TripNo.
export function countTripsInSteps(steps) {
  if (!Array.isArray(steps)) return 0;
  const seen = new Set();
  steps.forEach((step) => {
    if (!step) return;
    const tripNo = step.tripNo ?? step.TripNo ?? step.jobNo ?? step.JobNo;
    if (tripNo === null || tripNo === undefined || tripNo === '') return;
    seen.add(String(tripNo).trim().toUpperCase());
  });
  return seen.size;
}

// Priority: explicit server count > distinct trips in steps > flat trips[]; null if unknown.
export function resolveRouteTripCount(route, steps) {
  if (!route && !steps) return null;
  const r = route || {};

  const explicit = r.TripCount ?? r.tripCount ?? r.assignedTripCount;
  if (Number.isFinite(Number(explicit)) && explicit !== null && explicit !== '') {
    return Number(explicit);
  }

  const stepSource = Array.isArray(steps) && steps.length
    ? steps
    : (Array.isArray(r.itinerary) ? r.itinerary : []);
  const fromSteps = countTripsInSteps(stepSource);
  if (fromSteps > 0) return fromSteps;

  if (Array.isArray(r.trips) && r.trips.length > 0) return r.trips.length;

  return null;
}
